<template>
  <div>
    <!-- Tableau détail : une ligne par campagne (detail), filtrable -->
    <v-card class="pt-3 pb-3 mb-3">
      <v-row>
        <v-col cols="12" md="4">
          <v-select
            class="ml-6 mr-6"
            v-model="selectedSources"
            :items="sourceOptions"
            label="Source"
            multiple
            chips
            small-chips
            clearable
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
            class="ml-6 mr-6"
            v-model="selectedDetails"
            :items="detailOptions"
            label="Detail"
            multiple
            chips
            small-chips
            clearable
            hide-details
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            class="ml-6 mr-6"
            v-model="search"
            append-icon="mdi-magnify"
            label="Recherche"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <dot-menu
          @exportExcel="exportExcel"
          :position="'page-top-right'"
          :excelButton="{
            excelData,
            filename: `acqui-daily-report-${this.dateRange}.xlsx`,
          }"
        ></dot-menu>
        <v-col>
          <v-data-table
            class="ml-6 mr-6"
            :items="filteredItems"
            :headers="headers"
            :search="search"
          >
            <template
              v-slot:[`item.acqui_config_reporting.acqui_utm.source`]="{ item }"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div class="truncate-cell" v-bind="attrs" v-on="on">
                    {{ item.acqui_config_reporting.acqui_utm.source }}
                  </div>
                </template>
                <span>{{ item.acqui_config_reporting.acqui_utm.source }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.date="{ item }">
              <div class="truncateCell" :title="item.date">
                {{ formatDate(item.date) }}
              </div>
            </template>
            <template v-slot:item.detail="{ item }">
              <v-tooltip
                bottom
                v-if="item.detail && item.detail !== 'NoDetail'"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div class="truncate-cell" v-bind="attrs" v-on="on">
                    {{ item.detail }}
                  </div>
                </template>
                <span>{{ item.detail }}</span>
              </v-tooltip>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>

    <!-- 2ème tableau : totaux par source (somme de tous les details d'une source) -->
    <v-card class="pt-3 pb-3 mb-3">
      <v-row>
        <v-card-title>
          <h2 class="text-xl">Total par source (toutes campagnes)</h2>
        </v-card-title>
        <dot-menu
          @exportExcel="exportAggregatedExcel"
          :position="'page-top-right'"
          :excelButton="{
            excelData: aggregatedExcelData,
            filename: aggregatedExcelFilename,
            disabled: filteredAggregatedItems.length === 0,
          }"
        ></dot-menu>
      </v-row>
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            class="ml-6 mr-6"
            v-model="aggSelectedPlatforms"
            :items="aggPlatformOptions"
            label="Plateforme"
            multiple
            chips
            small-chips
            clearable
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            class="ml-6 mr-6"
            v-model="aggSelectedSites"
            :items="aggSiteOptions"
            label="Site"
            multiple
            chips
            small-chips
            clearable
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            class="ml-6 mr-6"
            v-model="aggSelectedSources"
            :items="aggSourceOptions"
            label="Source"
            multiple
            chips
            small-chips
            clearable
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            class="ml-6 mr-6"
            v-model="aggSearch"
            append-icon="mdi-magnify"
            label="Recherche"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table
            class="ml-6 mr-6"
            :items="filteredAggregatedItems"
            :headers="aggHeaders"
          >
            <template v-slot:item.value="{ item }">
              <span>{{ formatValue(item.value) }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<style scoped>
.truncate-cell {
  max-width: 150px; /* Limite la largeur de la cellule */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer; /* Indique que l'utilisateur peut interagir */
}
</style>

<script>
import DagInfo from "@/components/common/menus/DagInfo";
import useAxios from "@/hooks/useAxios";
import debounce from "lodash/debounce";
import * as XLSX from "xlsx";
import DotMenu from "@/components/common/menus/DotMenu";
import axios from "@/plugins/axios";

const DEFAULT_SOURCES = ["Taboola_mobile", "welcoming"];

export default {
  name: "dailyReportTable",
  components: { DotMenu },

  data() {
    return {
      excelData: new Blob([""], { type: "text/plain" }),
      aggregatedExcelData: new Blob([""], { type: "text/plain" }),
      search: "",
      aggSearch: "",
      aggSelectedPlatforms: [],
      aggSelectedSites: [],
      aggSelectedSources: [],
      items: [],
      csvData: "",
      selectedSources: [...DEFAULT_SOURCES],
      selectedDetails: [],
      headers: [
        { text: "date", value: "date" },
        { text: "config_name", value: "acqui_config_reporting.name" },
        {
          text: "platform",
          value: "acqui_config_reporting.acqui_platform.name",
        },
        { text: "site", value: "acqui_config_reporting.site.name" },
        { text: "metric", value: "acqui_config_reporting.acqui_metric.name" },

        { text: "source", value: "acqui_config_reporting.acqui_utm.source" },
        { text: "detail", value: "detail" },
        { text: "value", value: "value" },
      ],
      aggHeaders: [
        { text: "date", value: "date" },
        { text: "platform", value: "platform" },
        { text: "site", value: "site" },
        { text: "metric", value: "metric" },
        { text: "source", value: "source" },
        { text: "value", value: "value" },
      ],
    };
  },

  setup() {
    const { axiosGet, isLoading } = useAxios();

    return {
      axiosGet,
      isLoading,
    };
  },

  methods: {
    debouncedUpdateData: debounce(async function () {
      await this.getData();
    }, 500),

    async getData() {
      const params = {
        date_start: this.dateRange[0],
        date_end: this.dateRange[1],
      };
      const response = await this.axiosGet(
        "/acquisition/report/by_range_date",
        params
      );
      this.items = response.data;
      this.$store.dispatch("common/updateApplyFilters", false);
    },
    formatDate(date) {
      return date.split("T")[0];
    },
    formatValue(value) {
      return Number(value || 0).toFixed(2);
    },
    exportAggregatedExcel() {
      const rows = this.filteredAggregatedItems.map((item) => ({
        date: item.date,
        platform: item.platform,
        site: item.site,
        metric: item.metric,
        source: item.source,
        value: Number(item.value || 0),
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Total par source");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      this.aggregatedExcelData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    },
    async exportExcel() {
      // L'export reflète les filtres appliqués au tableau détail (source + detail).
      const params = {
        date_start: this.dateRange[0],
        date_end: this.dateRange[1],
        sources: this.selectedSources,
        details: this.selectedDetails,
      };

      const response = await axios.get("acquisition/report/excel", {
        params,
        // Sérialise les listes en `sources=a&sources=b` (attendu par FastAPI)
        // et encode les valeurs de detail (URLs possibles).
        paramsSerializer: (queryParams) => {
          const parts = [];
          for (const [key, value] of Object.entries(queryParams)) {
            if (Array.isArray(value)) {
              value.forEach((v) =>
                parts.push(
                  `${encodeURIComponent(key)}=${encodeURIComponent(v)}`
                )
              );
            } else if (value !== undefined && value !== null) {
              parts.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
              );
            }
          }
          return parts.join("&");
        },
        responseType: "blob",
      });

      this.excelData = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    },

    formatDetail(detail) {
      if (!detail || detail === "NoDetail") {
        return "-";
      }
      // Truncate long details for display
      return detail.length > 30 ? detail.substring(0, 30) + "..." : detail;
    },
  },
  async created() {
    // await this.getData();
  },
  computed: {
    filtersApplied() {
      return this.$store.getters["common/getApplyFilters"];
    },
    dateRange() {
      return this.$store.getters["acquisition/getDates"];
    },
    aggregatedExcelFilename() {
      return `acqui-daily-report-total-par-source-${this.dateRange.join(
        "_"
      )}.xlsx`;
    },
    sourceOptions() {
      const sources = new Set();
      for (const it of this.items) {
        const source = it.acqui_config_reporting?.acqui_utm?.source;
        if (source) sources.add(source);
      }
      return [...sources].sort();
    },
    detailOptions() {
      const details = new Set();
      for (const it of this.items) {
        if (it.detail && it.detail !== "NoDetail") details.add(it.detail);
      }
      return [...details].sort();
    },
    filteredItems() {
      return this.items.filter((it) => {
        const source = it.acqui_config_reporting?.acqui_utm?.source;
        const sourceOk =
          !this.selectedSources.length || this.selectedSources.includes(source);
        const detailOk =
          !this.selectedDetails.length ||
          this.selectedDetails.includes(it.detail);
        return sourceOk && detailOk;
      });
    },
    aggregatedItems() {
      const map = {};
      for (const it of this.items) {
        const config = it.acqui_config_reporting || {};
        const reportDate = it.date ? this.formatDate(String(it.date)) : "";
        const platform = config.acqui_platform?.name || "";
        const site = config.site?.name || "";
        const metric = config.acqui_metric?.name || "";
        const source = config.acqui_utm?.source || "";
        const key = [reportDate, platform, site, metric, source].join("||");
        if (!map[key]) {
          map[key] = {
            date: reportDate,
            platform,
            site,
            metric,
            source,
            value: 0,
          };
        }
        map[key].value += it.value || 0;
      }
      return Object.values(map);
    },
    aggPlatformOptions() {
      return [...new Set(this.aggregatedItems.map((item) => item.platform))]
        .filter(Boolean)
        .sort();
    },
    aggSiteOptions() {
      return [...new Set(this.aggregatedItems.map((item) => item.site))]
        .filter(Boolean)
        .sort();
    },
    aggSourceOptions() {
      return [...new Set(this.aggregatedItems.map((item) => item.source))]
        .filter(Boolean)
        .sort();
    },
    filteredAggregatedItems() {
      const search = this.aggSearch.trim().toLowerCase();

      return this.aggregatedItems.filter((item) => {
        const platformOk =
          !this.aggSelectedPlatforms.length ||
          this.aggSelectedPlatforms.includes(item.platform);
        const siteOk =
          !this.aggSelectedSites.length ||
          this.aggSelectedSites.includes(item.site);
        const sourceOk =
          !this.aggSelectedSources.length ||
          this.aggSelectedSources.includes(item.source);
        const searchOk =
          !search ||
          [
            item.date,
            item.platform,
            item.site,
            item.metric,
            item.source,
            item.value,
          ].some((value) => String(value).toLowerCase().includes(search));

        return platformOk && siteOk && sourceOk && searchOk;
      });
    },
  },
  watch: {
    filtersApplied() {
      this.debouncedUpdateData();
    },
    // dateRange() {
    //   console.log("dateRange", this.dateRange);
    // },
  },
};
</script>
